import { isFunction } from '@r-and-a-shared-ui/utils';
import { CTA, CTAProps } from '@r-and-a-shared-ui/common-ui';
import { useMutation } from '@tanstack/react-query';
import { useActionContext } from '../context/useActionsContext';
import { FormCtaActionTypes, getTempDisableOffCtaTypes } from '../config';
import { ActionHandlerParams } from '../types';
import { useFormContext } from 'react-hook-form';
import { useContext, useState } from 'react';
import {
  FormContentContext,
  FormContentContextType,
} from '../context/FormContentContext';
import {
  useAuthContext,
  useSiteConfigContext,
} from '@r-and-a-shared-ui/auth-client';
import { AUTH_TOKEN_KEY } from '../constants';

export interface FormCTAProps {
  key?: string;
  ctaProps?: CTAProps;
  actionType?: FormCtaActionTypes;
  isMembership?: boolean;
  onSubmissionSuccess?: () => void;
  toEmail?: string;
  fromEmail?: string;
  subject?: string;
  emailKey?: string;
}

export const FormCTA = (props: FormCTAProps) => {
  const {
    actionType,
    ctaProps,
    isMembership,
    onSubmissionSuccess,
    toEmail,
    fromEmail,
    subject,
    emailKey,
  } = props;

  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<
    boolean | null
  >(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);

  const { getValues, trigger, setError, clearErrors, setValue } =
    useFormContext();
  const {
    formContentProps,
    isLoading,
    errorsDictionary,
    successMessagesDictionary,
    nextStepsAfterSubmit,
    setSubmitSuccessMessage,
    initialFormState,
    customClientCTAHandler,
  } = useContext(FormContentContext) as FormContentContextType;

  const siteConfigContext = useSiteConfigContext();

  const memberRequiredAttributes =
    'memberRequiredAttributes' in (siteConfigContext ?? {})
      ? (siteConfigContext as any).memberRequiredAttributes
      : undefined;

  const context = useActionContext();
  const { API, FormCTAConfig } = context || {};

  const config = FormCTAConfig?.[actionType as FormCtaActionTypes];

  const ctaMutation: any = config?.trpcRoute
    ? API?.[config?.trpcRoute as keyof typeof API]
    : config?.clientSideMutation;

  const mutation = isFunction(ctaMutation?.useMutation)
    ? ctaMutation?.useMutation()
    : isFunction(ctaMutation)
    ? useMutation({ mutationFn: ctaMutation as any })
    : undefined;

  const storeTokensMutation = API?.['storeTokens']?.useMutation();
  const authTokenMutation = API?.[AUTH_TOKEN_KEY]?.useMutation();

  const addUserToAddressBookMutation =
    API?.['add_user_to_address_book']?.useMutation();

  const actionHandler = config?.actionHandler;

  let userEmail: string | undefined;

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { userState } = useAuthContext();
    userEmail = userState.user?.email;
  } catch (error) {
    // We are in SSO / PP context where useAuthContext is not available
  }

  const onClick = async () => {
    setHasAttemptedSubmit(true);

    const submitData = getValues ? getValues() : {};

    const hasNonEmptyField = Object.values(submitData).some(
      value => typeof value === 'string' && value.trim() !== '',
    );

    if (!hasNonEmptyField) {
      setError('submitData', {
        type: 'manual',
        message: 'At least one answer is required',
      });
      setIsSubmittedSuccessfully(false);
      return;
    }

    const params: ActionHandlerParams = {
      resource: ctaProps?.resource,
      submitData,
      mutation,
      storeTokensMutation,
      authTokenMutation,
      addUserToAddressBookMutation,
      memberRequiredAttributes,
      trigger,
      formContentProps,
      setSubmitSuccessMessage,
      setError,
      setValue,
      getValues,
      clearErrors,
      errorsDictionary,
      successMessagesDictionary,
      nextStepsAfterSubmit,
      initialFormState,
      userEmail,
      toEmail,
      fromEmail,
      subject,
      emailKey,
      customClientCTAHandler,
    };

    if (isFunction(actionHandler)) {
      if (setSubmitSuccessMessage) setSubmitSuccessMessage('');
      if (clearErrors) clearErrors('root.serverError');
      const result = await actionHandler(params);

      if (result?.hasErrors) {
        console.error(result.error);
        setIsSubmittedSuccessfully(false);
      } else {
        setIsSubmittedSuccessfully(true);

        if (onSubmissionSuccess) {
          onSubmissionSuccess();
        }
      }
    }
  };

  const tempDisableOff = getTempDisableOffCtaTypes().includes(
    actionType as FormCtaActionTypes,
  );

  return (
    <CTA
      {...ctaProps}
      key={ctaProps?.key}
      onClick={onClick}
      isDisabled={isLoading}
      isMembership={isMembership}
      isSubmittedSuccessfully={isSubmittedSuccessfully}
      hasAttemptedSubmit={hasAttemptedSubmit}
      tempDisableOff={tempDisableOff}
    />
  );
};

export default FormCTA;
